/*
 * AnalogSetup — movimiento ligado al scroll.
 *
 * 1. Scroll 3D (sections/analog-scroll-world.liquid): una secuencia de
 *    fotogramas renderizada en Blender que avanza con el scroll sobre un
 *    <canvas> fijo. El scroll solo mueve el tiempo de una toma continua.
 *    - Fundido entre fotogramas vecinos según la posición exacta: fluido aunque
 *      haya pocos fotogramas. Al parar, se asienta en un fotograma entero (nítido).
 *    - Pausas: dentro de cada tramo de texto la cámara se frena (mientras se lee)
 *      y acelera en las transiciones. Los extremos de cada tramo no se mueven.
 *    - HUD de cristal sincronizado con la animación (volumen al girar la
 *      perilla, tecla pulsada en cada pulsación).
 *    - Carga progresiva (1 de cada 16 fotogramas, luego 1 de cada 8…).
 *    - Solo trabaja (rAF) cuando la sección está en pantalla.
 *    - En móvil vertical usa la secuencia 9:16 si existe.
 *    - Es una animación que mueve el usuario con el scroll: se muestra también
 *      con «reducir movimiento», sin adornos.
 *
 * 2. Frase grande (sections/analog-tagline.liquid): cada palabra pasa de tono
 *    apagado a pleno al cruzar una línea de la pantalla, en orden de lectura.
 *
 * Sin listeners de scroll: un bucle rAF que solo corre mientras la sección
 * está visible (IntersectionObserver).
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isTouch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

  function clamp(v, a, b) {
    return v < a ? a : v > b ? b : v;
  }

  function smooth(t) {
    t = clamp(t, 0, 1);
    return t * t * (3 - 2 * t);
  }

  /* Dentro de un tramo: lento en los bordes, rápido en el centro. f(0)=0, f(1)=1. */
  function linger(u, amount) {
    return u - (amount * Math.sin(2 * Math.PI * u)) / (2 * Math.PI);
  }

  /* Primer tramo: arranca a velocidad plena (de hecho algo más) y frena al
     entrar en el primer texto, para enlazar con el ritmo de `linger` sin salto.
     f(0)=0, f(1)=1, f'(0)=1/(1-A/3) > 1. Sin esto el primer arrastre se movía a
     un cuarto de velocidad y parecía que la página no respondía. */
  function intro(u, amount) {
    u = clamp(u, 0, 1);
    var k = 1 - amount / 3;
    if (k <= 0) return u;
    return (u - (amount * u * u * u) / 3) / k;
  }

  function nums(str) {
    return String(str || '')
      .split(',')
      .map(function (x) {
        return parseFloat(x);
      })
      .filter(function (x) {
        return !isNaN(x);
      });
  }

  /* ------------------------------------------------------------ Scroll 3D */
  function initScrollWorld(root) {
    if (root.dataset.alSwBound) return;
    root.dataset.alSwBound = 'true';

    var canvas = root.querySelector('[data-al-sw-canvas]');
    var rail = root.querySelector('[data-al-sw-rail]');
    var hint = root.querySelector('[data-al-sw-hint]');
    var beats = Array.prototype.map.call(root.querySelectorAll('[data-al-sw-beat]'), function (el) {
      var s = (parseFloat(el.getAttribute('data-start')) || 0) / 100;
      var e = (parseFloat(el.getAttribute('data-end')) || 100) / 100;
      if (e <= s) e = Math.min(1, s + 0.1);
      return { el: el, s: s, e: e };
    });

    var lists = { desktop: [], mobile: [] };
    try {
      lists = JSON.parse(root.querySelector('[data-al-sw-frames]').textContent);
    } catch (err) {
      /* sin fotogramas: modo fijo */
    }

    var portrait = window.matchMedia('(max-width: 749px) and (orientation: portrait)').matches;
    var urls = portrait && lists.mobile && lists.mobile.length ? lists.mobile : lists.desktop || [];

    if (!urls.length || !canvas || !canvas.getContext) {
      root.classList.add('is-static');
      return;
    }

    /* ---- calidad segun la conexion -------------------------------------------
       Esto no es un video: no hay bitrate que bajar. Lo que se ajusta es cuantos
       fotogramas se descargan. El motor ya funde entre vecinos, asi que con uno
       de cada cuatro el movimiento sigue siendo continuo y pesa la cuarta parte.
       Con ahorro de datos o 2G no se descarga nada: queda el poster con los
       textos apilados, el mismo modo que ya se usa cuando no hay secuencia. */
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    var adaptive = root.getAttribute('data-adaptive') !== 'false';

    if (adaptive && conn && (conn.saveData || conn.effectiveType === 'slow-2g')) {
      root.classList.add('is-static');
      root.dataset.alQuality = 'estatico';
      return;
    }

    root.classList.add('is-live');

    /* ---- pausas: tramos entre los puntos medios de los huecos entre textos */
    var lingerAmount = clamp((parseFloat(root.getAttribute('data-linger')) || 0) / 100, 0, 0.9);
    var cuts = [0];
    for (var b = 0; b < beats.length - 1; b += 1) cuts.push((beats[b].e + beats[b + 1].s) / 2);
    cuts.push(1);

    function warp(p) {
      if (!lingerAmount) return p;
      for (var i = 0; i < cuts.length - 1; i += 1) {
        if (p <= cuts[i + 1] || i === cuts.length - 2) {
          var a = cuts[i];
          var z = cuts[i + 1];
          var u = z > a ? clamp((p - a) / (z - a), 0, 1) : 0;
          var f = i === 0 ? intro(u, lingerAmount) : linger(u, lingerAmount);
          return a + (z - a) * f;
        }
      }
      return p;
    }

    /* ---- HUD */
    var hudVol = root.querySelector('[data-al-hud="volume"]');
    var hudKeys = root.querySelector('[data-al-hud="keys"]');
    var vol = hudVol ? nums(hudVol.getAttribute('data-range')) : [];
    var volFill = hudVol && hudVol.querySelector('[data-al-hud-fill]');
    var volNum = hudVol && hudVol.querySelector('[data-al-hud-num]');
    var volWaves = hudVol ? hudVol.querySelectorAll('[data-al-hud-wave]') : [];
    var keyTimes = hudKeys ? nums(hudKeys.getAttribute('data-times')) : [];
    var keyItems = hudKeys ? hudKeys.querySelectorAll('[data-al-hud-key]') : [];
    var volShown = vol.length >= 4 ? vol[2] : 0;

    var lastKey = -1;

    function updateHud(t) {
      if (hudVol && vol.length >= 4) {
        var t0 = vol[0];
        var t1 = vol[1];
        var pad = 0.025;
        var on = t > t0 - pad && t < t1 + pad * 1.6;
        hudVol.classList.toggle('is-on', on);
        var u = smooth((t - t0) / (t1 - t0));
        var level = vol[2] + (vol[3] - vol[2]) * u;
        volShown += (level - volShown) * 0.25;
        if (volFill) volFill.style.transform = 'scaleX(' + (volShown / 100).toFixed(4) + ')';
        if (volNum) volNum.textContent = Math.round(volShown);
        for (var w = 0; w < volWaves.length; w += 1) {
          volWaves[w].style.opacity = volShown > 22 + w * 30 ? 1 : 0.18;
        }
      }
      if (hudKeys && keyTimes.length) {
        var active = -1;
        for (var k = 0; k < keyTimes.length; k += 1) {
          if (t > keyTimes[k] - 0.012 && t < keyTimes[k] + 0.022) active = k;
        }
        hudKeys.classList.toggle('is-on', active !== -1);
        if (active !== -1 && active !== lastKey) {
          for (var j = 0; j < keyItems.length; j += 1) keyItems[j].classList.toggle('is-current', j === active);
          lastKey = active;
        }
      }
    }

    var ctx = canvas.getContext('2d');
    var total = urls.length;
    var images = new Array(total);
    var ready = new Array(total);
    var started = false;
    var running = false;
    var drawnKey = '';
    var lastWidth = 0;
    var bgSampled = false;

    /* El render entra acelerando: los primeros fotogramas son casi iguales. La
       secuencia arranca en `offset` para que el primer arrastre ya se vea.
       Los anteriores no se descargan. */
    var offset = clamp((parseInt(root.getAttribute('data-frame-start'), 10) || 1) - 1, 0, Math.max(0, total - 2));
    var span = total - 1 - offset;

    /* ---- carga progresiva por pasadas ---------------------------------------
       Cada pasada rellena los huecos de la anterior, asi que se puede parar en
       cualquiera de ellas y la animacion sigue completa, solo que con menos
       fotogramas. `tier` es la pasada mas fina a la que se llegara. */
    var PASSES = [16, 8, 4, 2, 1];

    function tierFromConnection() {
      if (!adaptive || !conn) return 1;
      if (conn.effectiveType === '2g') return 8;
      if (conn.effectiveType === '3g') return 4;
      if (typeof conn.downlink === 'number' && conn.downlink > 0 && conn.downlink < 1.5) return 4;
      return 1;
    }

    var tier = tierFromConnection();
    var queue = [];
    var seen = {};
    var firstPass = 0;
    var deepest = 0;

    function addPass(stride) {
      if (deepest && stride >= deepest) return;
      for (var i = offset; i < total; i += stride) {
        if (!seen[i]) {
          seen[i] = true;
          queue.push(i);
        }
      }
      deepest = stride;
    }

    (function buildQueue() {
      /* Antes de medir no se baja del 1 de cada 4: si la conexion resulta ser
         mala, lo descargado de mas ya no se puede devolver. */
      var floorStride = tier > 4 ? tier : 4;
      PASSES.forEach(function (stride) {
        if (stride < floorStride) return;
        addPass(stride);
        if (stride === 16) firstPass = queue.length;
      });
      if (!seen[total - 1]) {
        seen[total - 1] = true;
        queue.splice(1, 0, total - 1);
      }
      root.dataset.alQuality = '1/' + tier;
    })();

    /* Lo que declara el navegador es una estimacion, y Safari y Firefox no
       tienen Network Information API. La velocidad real de descarga de los
       primeros fotogramas decide la calidad final. */
    var startedAt = 0;
    var retuned = false;

    function retune() {
      if (retuned || loaded < 10) return;
      retuned = true;
      var secs = ((window.performance ? performance.now() : Date.now()) - startedAt) / 1000;
      var rate = secs > 0 ? loaded / secs : 999;

      /* La medida manda sobre lo que declara el navegador: un movil en 4G con
         mala cobertura va mas lento que un 3G bueno, y el tipo declarado no lo
         sabe. Cada fotograma pesa ~13 KB, asi que 12 por segundo son ~1,2 Mbps. */
      tier = rate < 5 ? 8 : rate < 12 ? 4 : rate < 25 ? 2 : 1;

      if (tier > deepest) {
        /* Mas lento de lo previsto: se descarta lo que quedaba por pedir de las
           pasadas finas. Lo ya descargado se queda, claro. */
        queue = queue.filter(function (i) {
          return (i - offset) % tier === 0;
        });
      } else {
        PASSES.forEach(function (stride) {
          if (stride >= tier) addPass(stride);
        });
      }

      root.dataset.alQuality = '1/' + tier;
      pump();
    }

    var inFlight = 0;
    var MAX_IN_FLIGHT = 6;
    var loaded = 0;

    function pump() {
      if (!startedAt) startedAt = window.performance ? performance.now() : Date.now();
      while (inFlight < MAX_IN_FLIGHT && queue.length) {
        load(queue.shift());
      }
    }

    function load(i) {
      inFlight += 1;
      var img = new Image();
      img.decoding = 'async';
      img.onload = function () {
        inFlight -= 1;
        images[i] = img;
        ready[i] = true;
        loaded += 1;
        if (i === offset) sampleBackground(img);
        reportProgress();
        retune();
        drawnKey = '';
        pump();
      };
      img.onerror = function () {
        inFlight -= 1;
        pump();
      };
      img.src = urls[i];
    }

    /* ---- indicador de carga -------------------------------------------------
       Solo aparece si la primera pasada tarda: en conexiones rápidas no llega a
       verse. Se va cuando hay fotogramas suficientes para que el primer arrastre
       sea fluido (o, como tope, en cuanto haya imagen en pantalla). */
    var loadEl = root.querySelector('[data-al-sw-load]');
    var loadFill = root.querySelector('[data-al-sw-load-fill]');
    var loadDone = false;
    var showLoaderAt = (window.performance ? performance.now() : Date.now()) + 220;

    function hideLoader() {
      if (loadDone) return;
      loadDone = true;
      if (loadEl) loadEl.hidden = true;
    }

    function reportProgress() {
      if (loadDone || !loadEl) return;
      var pct = firstPass > 0 ? clamp(loaded / firstPass, 0, 1) : 1;
      if (loadFill) loadFill.style.transform = 'scaleX(' + pct.toFixed(3) + ')';
      var now = window.performance ? performance.now() : Date.now();
      if (pct >= 1) {
        hideLoader();
      } else if (now > showLoaderAt && loadEl.hidden) {
        loadEl.hidden = false;
      }
    }

    // Tope de seguridad: con imagen en pantalla, el indicador no debe insistir.
    window.setTimeout(hideLoader, 6000);

    function readyAtOrBelow(i) {
      for (var d = i; d >= offset; d -= 1) if (ready[d]) return d;
      for (var u = i + 1; u < total; u += 1) if (ready[u]) return u;
      return -1;
    }

    function readyAbove(i) {
      for (var u = i + 1; u < total; u += 1) if (ready[u]) return u;
      return -1;
    }

    /* El fondo de la sección toma el color real del render para que no haya borde. */
    function sampleBackground(img) {
      if (bgSampled) return;
      bgSampled = true;
      try {
        var c = document.createElement('canvas');
        c.width = 8;
        c.height = 8;
        var cx = c.getContext('2d');
        cx.drawImage(img, 0, 0, 8, 8);
        var p = cx.getImageData(0, 0, 1, 1).data;
        root.style.setProperty('--al-sw-bg', 'rgb(' + p[0] + ',' + p[1] + ',' + p[2] + ')');
      } catch (err) {
        /* lienzo contaminado por CORS: se queda el color del ajuste */
      }
    }

    /* ---- lienzo */
    function resize(force) {
      var w = canvas.clientWidth;
      var h = canvas.clientHeight;
      // En móvil la barra de direcciones cambia solo la altura: no recalcular.
      if (!force && isTouch && w === lastWidth) return;
      lastWidth = w;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      drawnKey = '';
    }

    function drawCover(img, alpha) {
      var cw = canvas.width;
      var ch = canvas.height;
      var s = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
      var w = img.naturalWidth * s;
      var h = img.naturalHeight * s;
      ctx.globalAlpha = alpha;
      ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
      ctx.globalAlpha = 1;
    }

    /* Pinta la posición fraccionaria: fotograma de abajo + el siguiente fundido. */
    function paint(pos) {
      var lo = Math.floor(pos);
      var a = readyAtOrBelow(lo);
      if (a === -1) return;
      var bIdx = a <= lo ? readyAbove(a) : -1;
      var frac = 0;
      if (bIdx !== -1) frac = clamp((pos - a) / (bIdx - a), 0, 1);
      var key = a + ':' + bIdx + ':' + frac.toFixed(2);
      if (key === drawnKey) return;
      drawnKey = key;
      drawCover(images[a], 1);
      if (frac > 0.01 && bIdx !== -1) drawCover(images[bIdx], frac);
      if (!root.classList.contains('is-drawn')) root.classList.add('is-drawn');
    }

    /* ---- progreso y tramos */
    function progress() {
      var rect = root.getBoundingClientRect();
      var span = root.offsetHeight - window.innerHeight;
      if (span <= 0) return 0;
      return clamp(-rect.top / span, 0, 1);
    }

    function updateBeats(p) {
      for (var k = 0; k < beats.length; k += 1) {
        var bt = beats[k];
        var local = (p - bt.s) / (bt.e - bt.s);
        var fadeIn = bt.s <= 0.001 ? 1 : smooth(local / 0.18);
        var fadeOut = bt.e >= 0.999 ? 1 : smooth((1 - local) / 0.18);
        var o = local < 0 || local > 1 ? 0 : Math.min(fadeIn, fadeOut);
        var y = (1 - o) * (local < 0.5 ? 24 : -24);
        bt.el.style.opacity = o.toFixed(3);
        bt.el.style.setProperty('--al-sw-y', y.toFixed(1) + 'px');
        bt.el.classList.toggle('is-active', o > 0.5);
      }
    }

    var pinned = false;
    function setPinned(on) {
      if (on === pinned) return;
      pinned = on;
      // Mientras la historia ocupa la pantalla, la barra fija de compra se aparta.
      document.documentElement.classList.toggle('al-sw-active', on);
    }

    /* Al parar el scroll entre dos fotogramas, termina de pasar al siguiente
       (en el sentido del scroll) para quedarse en una imagen nítida, sin fundido. */
    function snapTarget(pos, dir) {
      var a = readyAtOrBelow(Math.floor(pos));
      if (a === -1 || Math.abs(pos - a) < 0.02) return a === -1 ? pos : a;
      var b = readyAbove(a);
      if (b === -1) return a;
      if (Math.abs(b - pos) < 0.02) return b;
      return dir > 0 ? b : a;
    }

    var smoothP = 0;
    var lastP = -1;
    var dir = 1;
    var movedAt = 0;
    var shownPos = -1;
    var settleTo = null;
    function frame() {
      if (!running) return;
      var box = root.getBoundingClientRect();
      setPinned(box.top <= 1 && box.bottom >= window.innerHeight - 1);
      var p = progress();
      var now = window.performance ? performance.now() : Date.now();
      if (Math.abs(p - lastP) > 0.000001) {
        if (lastP >= 0) dir = p > lastP ? 1 : -1;
        lastP = p;
        movedAt = now;
      }
      // Suavizado del scroll (también con «reducir movimiento»: evita saltos de rueda).
      smoothP += (p - smoothP) * (reduceMotion ? 0.3 : 0.14);
      if (Math.abs(p - smoothP) < 0.0002) smoothP = p;
      var t = warp(smoothP);
      var pos = offset + t * span;
      /* El HUD se configura en tiempo 0-1 de la secuencia completa, asi que se
         le pasa la posicion absoluta: saltarse la entrada no lo desincroniza. */
      var tFull = total > 1 ? pos / (total - 1) : 0;
      if (shownPos < 0) shownPos = pos;
      if (now - movedAt > 120 && smoothP === p) {
        if (settleTo === null) settleTo = snapTarget(pos, dir);
        shownPos += (settleTo - shownPos) * 0.22;
        if (Math.abs(settleTo - shownPos) < 0.004) shownPos = settleTo;
      } else {
        settleTo = null;
        shownPos += (pos - shownPos) * 0.6;
        if (Math.abs(pos - shownPos) < 0.004) shownPos = pos;
      }
      paint(shownPos);
      updateBeats(smoothP);
      updateHud(tFull);
      if (rail) rail.style.transform = 'scaleY(' + smoothP.toFixed(4) + ')';
      if (hint) hint.style.opacity = smoothP > 0.015 ? '0' : '';
      window.requestAnimationFrame(frame);
    }

    function start() {
      if (!started) {
        started = true;
        resize(true);
        smoothP = progress();
        pump();
      }
      if (!running) {
        running = true;
        window.requestAnimationFrame(frame);
      }
    }

    function stop() {
      running = false;
      setPinned(false);
    }

    // Empieza a cargar un poco antes de llegar; pinta solo mientras se ve.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) start();
            else stop();
          });
        },
        { rootMargin: '100% 0px 100% 0px' }
      ).observe(root);
    } else {
      start();
    }

    window.addEventListener('resize', function () {
      resize(false);
    });
    window.addEventListener('orientationchange', function () {
      window.setTimeout(function () {
        resize(true);
      }, 250);
    });
  }

  /* ---------------------------------------------------------- Frase grande */
  function initTagline(root) {
    if (root.dataset.alTaglineBound) return;
    root.dataset.alTaglineBound = 'true';
    var words = Array.prototype.slice.call(root.querySelectorAll('[data-al-word]'));
    if (!words.length) return;

    if (!('IntersectionObserver' in window)) {
      words.forEach(function (w) {
        w.classList.add('is-on');
      });
      return;
    }

    var running = false;

    function tick() {
      if (!running) return;
      var line = window.innerHeight * 0.66;
      for (var i = 0; i < words.length; i += 1) {
        var r = words[i].getBoundingClientRect();
        var on = r.top + r.height / 2 < line;
        if (on !== words[i].classList.contains('is-on')) words[i].classList.toggle('is-on', on);
      }
      window.requestAnimationFrame(tick);
    }

    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var was = running;
        running = entry.isIntersecting;
        if (running && !was) window.requestAnimationFrame(tick);
      });
    }).observe(root);
  }

  function boot(scope) {
    var base = scope || document;
    base.querySelectorAll('[data-al-sw]').forEach(initScrollWorld);
    base.querySelectorAll('[data-al-tagline]').forEach(initTagline);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else {
    boot();
  }

  // Editor de temas: re-inicializar al recargar una sección.
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
